// ═══════════════════════════════════════════════════════════════════════════════
// BriefingDetailPage — single briefing edit + composition editor
// ═══════════════════════════════════════════════════════════════════════════════
//
// PURPOSE
//   The working surface for ONE briefing. Three sections:
//     1. Header strip: Code (mono), status (Draft/Published/Inactive), Version,
//        Publish button, Kill-switch toggle.
//     2. Metadata card: editable Name + Description (Code immutable post-create).
//     3. Composition table: ordered list of element rows, with per-row
//        override-tokens + IsActive + drag-to-reorder via Up/Down arrows.
//        "Add element" drawer surfaces every active element from the catalog
//        with a tier-aware preview.
//
// REORDER UX
//   V1 uses Up/Down arrows rather than true drag-and-drop — drag/drop with
//   antd Table requires extra wiring (dnd-kit). Arrow buttons hit the same
//   ReorderComposition endpoint with the post-swap order, so the wire shape
//   is forward-compatible if we later add drag/drop.
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowLeftOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { StatusPill } from '@/components/StatusPill';
import { HelpHotspot } from '@/features/help/HelpHotspot';
import { colors, typography } from '@/theme/tokens';
import {
  addCompositionElement,
  getBriefingById,
  getComposition,
  listElements,
  publishBriefing,
  removeCompositionElement,
  reorderComposition,
  setBriefingActive,
  updateBriefing,
  updateComposition,
  type BriefingContext,
  type BriefingContextElement,
  type CompositionRow,
} from '@/api/briefingContext';

const { Text, Title } = Typography;

export function BriefingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const briefingId = Number(id);
  const navigate = useNavigate();

  const [briefing, setBriefing] = useState<BriefingContext | null>(null);
  const [composition, setComposition] = useState<CompositionRow[]>([]);
  const [availableElements, setAvailableElements] = useState<BriefingContextElement[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingMeta, setSavingMeta] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const [metaForm] = Form.useForm<{ name?: string; description?: string }>();

  async function load() {
    if (!briefingId) return;
    setLoading(true);
    try {
      // Run the three reads in parallel — they have no inter-dependencies.
      const [b, comp, elements] = await Promise.all([
        getBriefingById(briefingId),
        getComposition(briefingId),
        listElements(false),
      ]);
      setBriefing(b);
      setComposition(comp);
      setAvailableElements(elements);
      metaForm.setFieldsValue({ name: b.name, description: b.description });
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Failed to load briefing');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [briefingId]);

  // ── Header actions ────────────────────────────────────────────────────────

  async function handleSaveMeta() {
    if (!briefing) return;
    try {
      const vals = await metaForm.validateFields();
      setSavingMeta(true);
      const saved = await updateBriefing({ id: briefing.id, ...vals });
      setBriefing(saved);
      message.success('Saved');
    } catch (e: unknown) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setSavingMeta(false);
    }
  }

  async function handlePublish() {
    if (!briefing) return;
    Modal.confirm({
      title: briefing.isDraft ? 'Publish this briefing?' : 'Re-publish this briefing?',
      content: briefing.isDraft
        ? 'Drafts are invisible to the runtime. Publishing makes this briefing available to every agent it’s linked to and bumps Version to ' +
          (briefing.version + 1) +
          '.'
        : 'Re-publishing bumps Version (currently v' +
          briefing.version +
          ') and stamps a new PublishedAt. Use this after metadata edits.',
      okText: 'Publish',
      onOk: async () => {
        try {
          const saved = await publishBriefing(briefing.id);
          setBriefing(saved);
          message.success(briefing.isDraft ? 'Published' : 'Re-published');
        } catch (e: unknown) {
          message.error(e instanceof Error ? e.message : 'Publish failed');
        }
      },
    });
  }

  async function handleToggleActive(next: boolean) {
    if (!briefing) return;
    try {
      await setBriefingActive(briefing.id, next);
      setBriefing({ ...briefing, isActive: next });
      message.success(next ? 'Briefing enabled' : 'Briefing disabled');
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Failed to update');
    }
  }

  // ── Composition actions ───────────────────────────────────────────────────

  async function handleAdd(payload: { elementId: number; overrideMaxTokens?: number | null }) {
    if (!briefing) return;
    try {
      const nextSort = (composition[composition.length - 1]?.sortOrder ?? 0) + 10;
      await addCompositionElement({
        briefingContextId: briefing.id,
        elementId: payload.elementId,
        sortOrder: nextSort,
        overrideMaxTokens: payload.overrideMaxTokens ?? null,
      });
      message.success('Element added');
      setAddOpen(false);
      await load();
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Add failed');
    }
  }

  async function handleRemoveComposition(row: CompositionRow) {
    try {
      await removeCompositionElement(row.id);
      setComposition((prev) => prev.filter((c) => c.id !== row.id));
      message.success(`Removed '${row.elementCode}'`);
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Remove failed');
    }
  }

  async function handleToggleCompositionActive(row: CompositionRow, next: boolean) {
    try {
      await updateComposition({ id: row.id, isActive: next });
      setComposition((prev) =>
        prev.map((c) => (c.id === row.id ? { ...c, isActive: next } : c)),
      );
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Failed to update');
    }
  }

  async function handleOverrideTokens(row: CompositionRow, value: number | null) {
    try {
      await updateComposition({ id: row.id, overrideMaxTokens: value });
      setComposition((prev) =>
        prev.map((c) => (c.id === row.id ? { ...c, overrideMaxTokens: value } : c)),
      );
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Failed to update');
    }
  }

  async function handleMove(index: number, direction: 'up' | 'down') {
    if (!briefing) return;
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= composition.length) return;
    // Reorder WITHIN a tier — moving across tiers via this gesture would
    // confuse the user because the visual order is (Tier ASC, SortOrder ASC).
    // The arrow buttons are only enabled for in-tier moves (see render).
    if (composition[index].elementTier !== composition[targetIndex].elementTier) return;

    const next = [...composition];
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];

    // Optimistic update, then reorder server-side. On failure, refetch to
    // recover the truth — simpler than tracking a snapshot for rollback.
    setComposition(next);
    try {
      await reorderComposition(
        briefing.id,
        next.map((c) => c.id),
      );
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Reorder failed');
      await load();
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading && !briefing) {
    return (
      <div style={{ padding: 80, display: 'flex', justifyContent: 'center' }}>
        <Spin />
      </div>
    );
  }

  if (!briefing) {
    return (
      <Alert
        type="error"
        showIcon
        message="Briefing not found"
        description="It may have been deleted, or you don't have access to this merchant."
        action={
          <Button onClick={() => navigate('/briefings')} icon={<ArrowLeftOutlined />}>
            Back to list
          </Button>
        }
      />
    );
  }

  // Composition is already sorted by (Tier ASC, SortOrder ASC) from the
  // server. Group for tier dividers in the table render.
  const tier1 = composition.filter((c) => c.elementTier === 1);
  const tier2 = composition.filter((c) => c.elementTier === 2);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageHeader
        accent="violet"
        icon={<ArrowLeftOutlined onClick={() => navigate('/briefings')} style={{ cursor: 'pointer' }} />}
        title={
          <Space size={10} align="center">
            <Text style={{ fontFamily: typography.fontMono, fontSize: 18, fontWeight: 600 }}>
              {briefing.code}
            </Text>
            {!briefing.isActive ? (
              <StatusPill tone="slate">Inactive</StatusPill>
            ) : briefing.isDraft ? (
              <StatusPill tone="amber">Draft</StatusPill>
            ) : (
              <StatusPill tone="green">Published</StatusPill>
            )}
            <Tag color="default" style={{ marginRight: 0 }}>
              v{briefing.version}
            </Tag>
            <HelpHotspot topic="briefing-context.briefings" />
          </Space>
        }
        subtitle={briefing.name ?? '(no name yet)'}
        actions={
          <Space size={8}>
            <Tooltip title={briefing.isActive ? 'Disable everywhere' : 'Re-enable'}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <Switch
                  checked={briefing.isActive}
                  onChange={(v) => void handleToggleActive(v)}
                />
                Active
              </span>
            </Tooltip>
            <Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading} />
            <Button
              type="primary"
              icon={<CloudUploadOutlined />}
              onClick={() => void handlePublish()}
              disabled={!briefing.isActive}
            >
              {briefing.isDraft ? 'Publish' : 'Re-publish'}
            </Button>
          </Space>
        }
      />

      {briefing.isDraft && (
        <Alert
          type="info"
          showIcon
          message="This briefing is a draft."
          description="Drafts are invisible to the runtime. Compose the elements you want, then click Publish to make it available to agents that link this briefing."
        />
      )}

      {/* ── Metadata card ─────────────────────────────────────────────── */}
      <SectionCard>
        <div style={{ padding: '4px 4px 0' }}>
          <Title level={5} style={{ marginTop: 0 }}>
            Metadata
          </Title>
          <Form
            form={metaForm}
            layout="vertical"
            initialValues={{ name: briefing.name, description: briefing.description }}
          >
            <Form.Item name="name" label="Name" rules={[{ max: 200 }]}>
              <Input placeholder="Human-readable name" />
            </Form.Item>
            <Form.Item name="description" label="Description" rules={[{ max: 1000 }]}>
              <Input.TextArea rows={2} placeholder="What this briefing provides to the agent…" />
            </Form.Item>
            <div style={{ textAlign: 'right' }}>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={savingMeta}
                onClick={() => void handleSaveMeta()}
              >
                Save metadata
              </Button>
            </div>
          </Form>
        </div>
      </SectionCard>

      {/* ── Composition editor ────────────────────────────────────────── */}
      <SectionCard noPadding>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 16px',
            borderBottom: `1px solid ${colors.slate[200]}`,
          }}
        >
          <Title level={5} style={{ margin: 0 }}>
            Composition
          </Title>
          <Text type="secondary" style={{ fontSize: 12.5 }}>
            {composition.length === 0
              ? 'No elements yet — add one to start composing.'
              : `${composition.length} element(s) · Stable renders first, then Per-turn.`}
          </Text>
          <span style={{ flex: 1 }} />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
            Add element
          </Button>
        </div>

        {composition.length === 0 ? (
          <div style={{ padding: '40px 16px', textAlign: 'center' }}>
            <Text type="secondary">Empty composition. Click &ldquo;Add element&rdquo; above.</Text>
          </div>
        ) : (
          <>
            {tier1.length > 0 && (
              <CompositionTable
                rows={tier1}
                allRows={composition}
                tier={1}
                onMove={handleMove}
                onToggleActive={handleToggleCompositionActive}
                onOverrideTokens={handleOverrideTokens}
                onRemove={handleRemoveComposition}
              />
            )}
            {tier2.length > 0 && (
              <CompositionTable
                rows={tier2}
                allRows={composition}
                tier={2}
                onMove={handleMove}
                onToggleActive={handleToggleCompositionActive}
                onOverrideTokens={handleOverrideTokens}
                onRemove={handleRemoveComposition}
              />
            )}
          </>
        )}
      </SectionCard>

      <AddElementDrawer
        open={addOpen}
        availableElements={availableElements}
        compositionElementIds={new Set(composition.map((c) => c.briefingContextElementId))}
        onClose={() => setAddOpen(false)}
        onAdd={handleAdd}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CompositionTable — one tier at a time so the in-tier reorder is intuitive
// ─────────────────────────────────────────────────────────────────────────────

interface CompositionTableProps {
  rows: CompositionRow[];
  allRows: CompositionRow[]; // for absolute-index lookup when moving
  tier: 1 | 2;
  onMove: (index: number, direction: 'up' | 'down') => void;
  onToggleActive: (row: CompositionRow, next: boolean) => void;
  onOverrideTokens: (row: CompositionRow, value: number | null) => void;
  onRemove: (row: CompositionRow) => void;
}

function CompositionTable({
  rows,
  allRows,
  tier,
  onMove,
  onToggleActive,
  onOverrideTokens,
  onRemove,
}: CompositionTableProps) {
  const columns: ColumnsType<CompositionRow> = [
    {
      title: 'Order',
      key: 'order',
      width: 90,
      render: (_v, row) => {
        const absIndex = allRows.findIndex((r) => r.id === row.id);
        const tierIndex = rows.findIndex((r) => r.id === row.id);
        return (
          <Space size={2}>
            <Button
              size="small"
              type="text"
              icon={<ArrowUpOutlined />}
              disabled={tierIndex === 0}
              onClick={() => onMove(absIndex, 'up')}
            />
            <Button
              size="small"
              type="text"
              icon={<ArrowDownOutlined />}
              disabled={tierIndex === rows.length - 1}
              onClick={() => onMove(absIndex, 'down')}
            />
          </Space>
        );
      },
    },
    {
      title: 'Element',
      dataIndex: 'elementDisplayName',
      key: 'elementDisplayName',
      render: (_displayName: string | undefined, row) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontWeight: 600, fontSize: 13, color: colors.slate[900] }}>
            {row.elementDisplayName || row.elementCode}
          </Text>
          <Text
            type="secondary"
            style={{ fontFamily: typography.fontMono, fontSize: 11.5 }}
          >
            {row.elementCode}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'elementCategory',
      key: 'elementCategory',
      width: 160,
      render: (cat?: string) =>
        cat ? <StatusPill tone="slate">{cat}</StatusPill> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Tokens',
      key: 'tokens',
      width: 160,
      render: (_v, row) => (
        <Tooltip
          title={
            row.overrideMaxTokens != null
              ? `Overrides element default (${row.elementDefaultMaxTokens}). Clear to revert.`
              : `Using element default (${row.elementDefaultMaxTokens}). Set a value to override.`
          }
        >
          <InputNumber
            size="small"
            placeholder={String(row.elementDefaultMaxTokens)}
            value={row.overrideMaxTokens ?? undefined}
            onChange={(v) => onOverrideTokens(row, typeof v === 'number' ? v : null)}
            min={0}
            max={10000}
            style={{ width: 130 }}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 80,
      align: 'center',
      render: (v: boolean, row) => (
        <Switch size="small" checked={v} onChange={(next) => onToggleActive(row, next)} />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      align: 'right',
      render: (_v, row) => (
        <Popconfirm
          title={`Remove '${row.elementCode}' from this briefing?`}
          okText="Remove"
          okButtonProps={{ danger: true }}
          onConfirm={() => onRemove(row)}
        >
          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          padding: '8px 16px',
          background: colors.slate[50],
          borderBottom: `1px solid ${colors.slate[200]}`,
          fontSize: 11,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
          color: colors.slate[500],
          fontWeight: 600,
        }}
      >
        {tier === 1 ? 'Stable — cached during the conversation' : 'Per-turn — refreshed every message'}
      </div>
      <Table<CompositionRow>
        rowKey="id"
        columns={columns}
        dataSource={rows}
        size="middle"
        pagination={false}
        rowClassName={(row) => (row.isActive ? '' : 'briefing-comp-inactive')}
      />
      <style>{`.briefing-comp-inactive { opacity: 0.6; }`}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AddElementDrawer — pick from the catalog of active elements
// ─────────────────────────────────────────────────────────────────────────────

interface AddDrawerProps {
  open: boolean;
  availableElements: BriefingContextElement[];
  compositionElementIds: Set<number>;
  onClose: () => void;
  onAdd: (payload: { elementId: number; overrideMaxTokens?: number | null }) => void;
}

function AddElementDrawer({
  open,
  availableElements,
  compositionElementIds,
  onClose,
  onAdd,
}: AddDrawerProps) {
  const [form] = Form.useForm<{ elementId: number; overrideMaxTokens?: number | null }>();
  const watchedId = Form.useWatch('elementId', form);

  useEffect(() => {
    if (!open) form.resetFields();
  }, [open, form]);

  // Already-composed elements are disabled in the picker — the manager rejects
  // duplicates with a clear error, but it's friendlier to disable upfront.
  const options = useMemo(
    () =>
      availableElements
        .filter((e) => e.isActive)
        .map((e) => ({
          value: e.id,
          label: e.code,
          disabled: compositionElementIds.has(e.id),
          element: e,
        })),
    [availableElements, compositionElementIds],
  );

  const selected = availableElements.find((e) => e.id === watchedId);

  function handleOk() {
    form
      .validateFields()
      .then((vals) => onAdd(vals))
      .catch(() => {});
  }

  return (
    <Drawer
      title="Add element"
      width={520}
      open={open}
      onClose={onClose}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={handleOk}>
            Add
          </Button>
        </Space>
      }
    >
      <Form layout="vertical" form={form}>
        <Form.Item
          name="elementId"
          label="Element"
          rules={[{ required: true, message: 'Pick an element' }]}
          tooltip="Already-added elements are disabled in this list."
        >
          <Select
            showSearch
            placeholder="Pick an element from the catalog"
            options={options}
            optionFilterProp="label"
            optionRender={(opt) => {
              const el = (opt.data as unknown as { element: BriefingContextElement }).element;
              return (
                <Space direction="vertical" size={0} style={{ padding: '2px 0' }}>
                  <Text style={{ fontFamily: typography.fontMono, fontSize: 12.5 }}>
                    {el.code}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 11.5 }}>
                    T{el.tier} · {el.category ?? '—'} · default {el.defaultMaxTokens} tokens
                  </Text>
                </Space>
              );
            }}
          />
        </Form.Item>

        {selected && (
          <Card size="small" style={{ marginBottom: 16 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {selected.description || '(no description provided)'}
            </Text>
          </Card>
        )}

        <Form.Item
          name="overrideMaxTokens"
          label="Token budget override (optional)"
          tooltip={
            selected
              ? `Leave blank to use the element default (${selected.defaultMaxTokens}).`
              : 'Leave blank to use the element default.'
          }
        >
          <InputNumber
            min={0}
            max={10000}
            placeholder={selected ? String(selected.defaultMaxTokens) : 'Use default'}
            style={{ width: '100%' }}
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
